import { useState, useRef, useEffect } from "react"
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Avatar,
  Divider,
  CircularProgress,
} from "@mui/material"
import { useSearchParams, useNavigate } from "react-router-dom"
import axios from "axios"
import { Visibility, VisibilityOff, LockOutlined, ErrorOutline, EmailOutlined } from "@mui/icons-material"
import { Turnstile } from "@marsidev/react-turnstile"
import LoginImage from "../assets/Data_security_05.jpg"

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001"
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const Login = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const roleFromQuery = searchParams.get("role") || ""
  const turnstileRef = useRef(null)

  // ============ STATE ============
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [role, setRole] = useState(roleFromQuery)
  const [error, setError] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState(null)
  
  // SSO States
  const [loading, setLoading] = useState(false)
  const [checkingSSO, setCheckingSSO] = useState(false)
  const [showSSOButtons, setShowSSOButtons] = useState(false)
  const [ssoProvider, setSsoProvider] = useState(null)
  const [showPasswordField, setShowPasswordField] = useState(true)
  const [emailSubmitted, setEmailSubmitted] = useState(false)

  // Forgot password states
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [forgotEmail, setForgotEmail] = useState("")
  const [showResetPassword, setShowResetPassword] = useState(false)
  const [resetOtp, setResetOtp] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [resetError, setResetError] = useState("")
  const [isResendingResetOTP, setIsResendingResetOTP] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // ============ CHECK SSO AVAILABILITY ============
  const checkSSOAvailability = async (email) => {
    if (!email) {
      setError("Please enter your email")
      return
    }

    setCheckingSSO(true)
    setError("")

    try {
      const response = await axios.post(`${API_URL}/api/sso/check`, { email })
      if (response.data.sso_available) {
        setShowSSOButtons(true)
        setSsoProvider(response.data.sso_provider)
        setShowPasswordField(false)
        setEmailSubmitted(true)
        setError("")
      } else {
        setShowSSOButtons(false)
        setShowPasswordField(true)
        setEmailSubmitted(true)
        // If user exists but SSO not enabled, they can use password
        if (response.data.user_exists) {
          // Allow password login
        } else {
          setError("User not found. Please check your email or sign up.")
        }
      }
    } catch (error) {
      //console.error("SSO check error:", error)
      setShowSSOButtons(false)
      setShowPasswordField(true)
      if (error.response?.data?.message) {
        setError(error.response.data.message)
      }
    } finally {
      setCheckingSSO(false)
    }
  }

  // ============ HANDLE EMAIL SUBMIT ============
  const handleEmailSubmit = (e) => {
    e.preventDefault()
    checkSSOAvailability(email)
  }

  // ============ HANDLE SSO LOGIN ============
  const handleSSOLogin = async (provider) => {
    setLoading(true)
    setError("")

    try {
      const response = await axios.post(`${API_URL}/api/sso/initiate`, {
        email: email,
        provider: provider,
      },{withCredentials: true})
      
      // Redirect to provider's login page
      window.location.href = response.data.auth_url
    } catch (error) {
      //console.error("SSO initiate error:", error)
      setError(error.response?.data?.message || "Failed to initiate SSO login")
      setLoading(false)
    }
  }

  // ============ HANDLE PASSWORD LOGIN ============
  const handlePasswordSubmit = async (e) => {
    e.preventDefault()
    setError("")

    if (!turnstileToken) {
      setError("Please complete the security verification.")
      return
    }

    try {
      const response = await axios.post(
        `${API_URL}/api/user/login`,
        {
          email,
          password,
          turnstileToken: turnstileToken,
        },
        { withCredentials: true }
      )

      if (turnstileRef.current) {
        turnstileRef.current.reset()
      }
      setTurnstileToken(null)

      const { token, user } = response.data
      
      localStorage.setItem("token", token)
      localStorage.setItem("role", user.role)
      localStorage.setItem("user", JSON.stringify(user))
      if (user.subscription) {
        localStorage.setItem("subscription", JSON.stringify(user.subscription))
      } else {
        localStorage.removeItem("subscription")
      }
      if (user.tenant_id) {
        localStorage.setItem("tenant_id", user.tenant_id)
      } else {
        localStorage.removeItem("tenant_id")
      }

      window.dispatchEvent(new Event("storage"))
      window.dispatchEvent(new CustomEvent("authChange", { detail: user }))
      
      if (user.role === "admin") {
        navigate("/companydetails")
      } else {
        navigate("/home")
      }      
    } catch (err) {
      if (turnstileRef.current) {
        turnstileRef.current.reset()
      }
      setTurnstileToken(null)

      if (err.response && err.response.data && err.response.data.message) {
        setError(err.response.data.message)
      } else {
        setError("Login failed. Please check your credentials.")
      }
      //console.error(err)
    }
  }

  // ============ TURNSTILE HANDLERS ============
  const handleTurnstileSuccess = (token) => {
    setTurnstileToken(token)
    setError("")
  }

  const handleTurnstileExpire = () => {
    setTurnstileToken(null)
    setError("Security verification expired. Please complete it again.")
  }

  const handleTurnstileError = (error) => {
    //console.error("Turnstile error:", error)
    setTurnstileToken(null)
    setError("Security verification failed. Please refresh and try again.")
  }

  // ============ FORGOT PASSWORD HANDLERS ============
  const handleForgotPassword = async () => {
    try {
      await axios.post(`${API_URL}/api/user/forgot-password`, {
        email: forgotEmail,
      })

      setShowForgotPassword(false)
      setShowResetPassword(true)
      setError("")
      setResetError("")
      setResetOtp("")
    } catch (err) {
      setError("Failed to send reset email. Please check your email address.")
      //console.error(err)
    }
  }

  const handleResetPassword = async () => {
    setResetError("")

    if (newPassword !== confirmPassword) {
      setResetError("Passwords do not match")
      return
    }

    if (newPassword.length < 8) {
      setResetError("Password must be at least 8 characters long")
      return
    }

    try {
      await axios.post(`${API_URL}/api/user/reset-password`, {
        email: forgotEmail,
        otp: resetOtp,
        newPassword: newPassword,
      })

      setShowResetPassword(false)
      setResetError("")
      alert("Password reset successfully! Please login with your new password.")

      setForgotEmail("")
      setResetOtp("")
      setNewPassword("")
      setConfirmPassword("")
    } catch (err) {
      if (err.response && err.response.data && err.response.data.message) {
        const errorMessage = err.response.data.message
        if (errorMessage.includes("expired")) {
          setResetError("OTP has expired. Please request a new one.")
        } else if (errorMessage.includes("Invalid OTP")) {
          setResetError("Invalid OTP. Please check and try again.")
        } else {
          setResetError(errorMessage)
        }
      } else {
        setResetError("Password reset failed. Please try again.")
      }
      //console.error(err)
    }
  }

  const handleResendResetOTP = async () => {
    setIsResendingResetOTP(true)
    setResetError("")

    try {
      await axios.post(`${API_URL}/api/user/forgot-password`, {
        email: forgotEmail,
      })

      setResetError("")
      setResetOtp("")
      alert("New OTP has been sent to your email.")
    } catch (err) {
      if (err.response && err.response.data && err.response.data.message) {
        setResetError(err.response.data.message)
      } else {
        setResetError("Failed to resend OTP. Please try again.")
      }
      //console.error(err)
    } finally {
      setIsResendingResetOTP(false)
    }
  }

  const handleForgotPasswordDialogClose = () => {
    setShowForgotPassword(false)
    setForgotEmail("")
  }

  const handleResetPasswordDialogClose = () => {
    setShowResetPassword(false)
    setResetOtp("")
    setNewPassword("")
    setConfirmPassword("")
    setResetError("")
    setShowNewPassword(false)
    setShowConfirmPassword(false)
  }

  // ============ RENDER ============
  return (
    <Box sx={{ minHeight: '81vh', display: 'flex', boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)" }}>
      <Grid container sx={{ flex: 1 }}>
        <Grid item xs={12} md={7} sx={{ display: 'flex', alignItems: 'center', bgcolor: 'background.default' }}>
          <Container maxWidth="sm" sx={{ py: 4 }}>
            <Box sx={{ px: { xs: 2, sm: 4 } }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                <Avatar sx={{ bgcolor: "#1976d2", width: 56, height: 56 }}>
                  <LockOutlined />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {role ? `${role.charAt(0).toUpperCase() + role.slice(1)} Login` : "Sign in"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Enter your credentials to continue
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              {error && (
                <Alert severity="error" sx={{ mb: 2 }} icon={<ErrorOutline fontSize="small" />}>
                  {error}
                </Alert>
              )}

              {/* ============ EMAIL FIRST FLOW ============ */}
              {!emailSubmitted ? (
                // Step 1: Enter Email
                <form onSubmit={handleEmailSubmit} noValidate>
                  <TextField
                    label="Email"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    size="medium"
                    disabled={checkingSSO}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {checkingSSO ? (
                            <CircularProgress size={24} />
                          ) : (
                            <IconButton type="submit" edge="end">
                              <EmailOutlined />
                            </IconButton>
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, color: 'text.secondary', fontSize: '0.85rem' }}
                  >
                    Enter your work email to continue
                  </Typography>
                </form>
              ) : (
                // Step 2: Show SSO or Password
                <>
                  {/* Show email with edit option */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {email}
                    </Typography>
                    <Button 
                      size="small" 
                      onClick={() => {
                        setEmailSubmitted(false)
                        setShowSSOButtons(false)
                        setShowPasswordField(true)
                        setSsoProvider(null)
                      }}
                      sx={{ textTransform: 'none' }}
                    >
                      Change
                    </Button>
                  </Box>

                  {showSSOButtons && ssoProvider ? (
                    // Show SSO Buttons
                    <>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Sign in with {ssoProvider.charAt(0).toUpperCase() + ssoProvider.slice(1)}
                      </Typography>
                      
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={
                          ssoProvider === 'google' ? (
                            <img 
                              src="https://www.google.com/favicon.ico" 
                              alt="Google" 
                              style={{ width: 20, height: 20 }}
                            />
                          ) : (
                            <img 
                              src="https://www.microsoft.com/favicon.ico" 
                              alt="Microsoft" 
                              style={{ width: 20, height: 20 }}
                            />
                          )
                        }
                        onClick={() => handleSSOLogin(ssoProvider)}
                        disabled={loading}
                        sx={{
                          py: 1.5,
                          bgcolor: '#1976d2',
                          '&:hover': { bgcolor: '#1565c0' },
                          textTransform: 'none',
                          fontSize: '1rem',
                        }}
                      >
                        {loading ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          `Sign in with ${ssoProvider.charAt(0).toUpperCase() + ssoProvider.slice(1)}`
                        )}
                      </Button>

                      <Divider sx={{ my: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          OR
                        </Typography>
                      </Divider>

                      <Button
                        variant="text"
                        fullWidth
                        onClick={() => {
                          setShowSSOButtons(false)
                          setShowPasswordField(true)
                        }}
                        sx={{ textTransform: 'none' }}
                      >
                        Use password instead
                      </Button>
                    </>
                  ) : (
                    // Show Password Login
                    <form onSubmit={handlePasswordSubmit} noValidate>
                      <TextField
                        label="Password"
                        type={showPassword ? "text" : "password"}
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        size="medium"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton 
                                aria-label="toggle password visibility" 
                                onClick={() => setShowPassword(!showPassword)} 
                                edge="end" 
                                size="large"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Typography
                        sx={{ cursor: "pointer", color: "primary.main", textDecoration: "none", ml: "370px", color: "black", fontWeight: "bold", fontSize: "12px", mt: "2px" }}
                        onClick={() => setShowForgotPassword(true)}
                      >
                        Forgot Password?
                      </Typography>

                      {TURNSTILE_SITE_KEY && (
                        <Box sx={{ mt: 2, mb: 2, display: 'flex', justifyContent: 'center' }}>
                          <Turnstile
                            ref={turnstileRef}
                            siteKey={TURNSTILE_SITE_KEY}
                            onSuccess={handleTurnstileSuccess}
                            onExpire={handleTurnstileExpire}
                            onError={handleTurnstileError}
                            options={{
                              theme: 'auto',
                              size: 'normal',
                            }}
                          />
                        </Box>
                      )}

                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexDirection: "row" }}>
                        <Button 
                          type="submit" 
                          variant="contained" 
                          disabled={!turnstileToken}
                          sx={{ textTransform: "none", fontSize: '1rem', width: "200px", mt: "7px", bgcolor: "primary" }}
                        >
                          Sign in
                        </Button>
                        <Button variant="outlined" onClick={() => navigate("/")} sx={{ mt: "7px", textTransform: "none", fontSize: '1rem', width: "200px", borderColor: "primary", color: "primary" }}>
                          Cancel
                        </Button>
                      </Box>

                      <Button
                        variant="text"
                        fullWidth
                        onClick={() => {
                          setEmailSubmitted(false)
                          setShowSSOButtons(false)
                          setShowPasswordField(true)
                        }}
                        sx={{ mt: 2, textTransform: 'none' }}
                      >
                        Use different email
                      </Button>
                    </form>
                  )}
                </>
              )}

              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary" }}
                >
                  Don't have an account?{" "}
                  <Box component="span" onClick={() => navigate("/register")} sx={{ color: "black", fontWeight: "bold", cursor: "pointer", textDecoration: "none" }}>
                    SignUp
                  </Box>
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1.5, mt: 3, pt: 2, borderTop: "1px solid #e2e8f0" }}>
                <Typography
                  variant="caption"
                  onClick={() => navigate("/terms")}
                  sx={{ cursor: "pointer", color: "text.secondary", "&:hover": { color: "primary.main", textDecoration: "underline" } }}
                >
                  Terms of Use
                </Typography>
                <Typography variant="caption" sx={{ color: "text.disabled" }}>•</Typography>
                <Typography
                  variant="caption"
                  onClick={() => navigate("/privacy")}
                  sx={{ cursor: "pointer", color: "text.secondary", "&:hover": { color: "primary.main", textDecoration: "underline" } }}
                >
                  Privacy Policy
                </Typography>
              </Box>
            </Box>
          </Container>
        </Grid>

        <Grid item xs={12} md={4} sx={{ display: { xs: "none", md: "flex" }, justifyContent: "center", alignItems: "center" }}>
          <Box
            component="img"
            src={LoginImage}
            alt="Login illustration"
            sx={{
              width: '100%',
              height: 'auto',
              borderRadius: 2,
            }}
          />
        </Grid>
      </Grid>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onClose={handleForgotPasswordDialogClose}>
        <DialogTitle>Forgot Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter your email address to receive a password reset OTP.
          </Typography>
          <TextField 
            label="Email" 
            variant="outlined" 
            fullWidth 
            value={forgotEmail} 
            onChange={(e) => setForgotEmail(e.target.value)} 
            type="email" 
            size="small" 
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleForgotPasswordDialogClose}>Cancel</Button>
          <Button onClick={handleForgotPassword} variant="contained">
            Send OTP
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPassword} onClose={handleResetPasswordDialogClose}>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter the OTP sent to your email and your new password.
          </Typography>

          {resetError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {resetError}
            </Alert>
          )}

          <TextField
            label="OTP"
            variant="outlined"
            fullWidth
            margin="normal"
            value={resetOtp}
            onChange={(e) => setResetOtp(e.target.value)}
            inputProps={{
              maxLength: 6,
              autoComplete: "off",
            }}
            autoComplete="off"
            size="small"
            error={resetError && (resetError.includes("OTP") || resetError.includes("expired"))}
          />

          <Box sx={{ mt: 1, mb: 2, display: "flex", justifyContent: "center" }}>
            <Button variant="text" color="primary" onClick={handleResendResetOTP} disabled={isResendingResetOTP} sx={{ textTransform: "none" }}>
              {isResendingResetOTP ? "Resending..." : "Resend OTP"}
            </Button>
          </Box>

          <TextField
            label="New Password"
            type={showNewPassword ? "text" : "password"}
            variant="outlined"
            fullWidth
            margin="normal"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            size="small"
            error={resetError && (resetError.includes("password") || resetError.includes("match"))}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton aria-label="toggle new password visibility" onClick={() => setShowNewPassword(!showNewPassword)} edge="end" size="large">
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Confirm New Password"
            type={showConfirmPassword ? "text" : "password"}
            variant="outlined"
            fullWidth
            margin="normal"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            size="small"
            error={resetError && resetError.includes("match")}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton aria-label="toggle confirm password visibility" onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" size="large">
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResetPasswordDialogClose}>Cancel</Button>
          <Button onClick={handleResetPassword} variant="contained" disabled={!resetOtp || resetOtp.length !== 6 || !newPassword || !confirmPassword}>
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Login